/**
 * normalizeCollege.js
 *
 * Normalizes user-typed college names for comparison in Hard Mode.
 * Handles common abbreviations, nicknames, and alternate names
 * that fans commonly use for college programs.
 *
 * All normalization maps to the canonical CSV values.
 */

/**
 * Internal normalization map: maps common aliases/nicknames → canonical CSV name.
 * Keys are lowercased for case-insensitive lookup.
 */
const ALIAS_MAP = {
  // USC (canonical matches CSV data value "USC")
  'usc': 'USC',
  'southern cal': 'USC',
  'southern california': 'USC',
  'university of southern california': 'USC',
  'sc': 'USC',

  // Ole Miss
  'ole miss': 'Ole Miss',
  'mississippi': 'Ole Miss',
  'university of mississippi': 'Ole Miss',

  // Miami handling
  'miami': 'Miami (FL)',
  'the u': 'Miami (FL)',
  'miami fl': 'Miami (FL)',
  'miami florida': 'Miami (FL)',
  'university of miami': 'Miami (FL)',
  'miami oh': 'Miami (OH)',
  'miami ohio': 'Miami (OH)',
  'miami of ohio': 'Miami (OH)',
  'miami redhawks': 'Miami (OH)',

  // LSU
  'lsu': 'LSU',
  'louisiana state': 'LSU',
  'louisiana state university': 'LSU',

  // BYU
  'byu': 'BYU',
  'brigham young': 'BYU',
  'brigham young university': 'BYU',

  // TCU
  'tcu': 'TCU',
  'texas christian': 'TCU',
  'texas christian university': 'TCU',

  // SMU
  'smu': 'SMU',
  'southern methodist': 'SMU',

  // NC State
  'nc state': 'NC State',
  'north carolina state': 'NC State',
  'ncsu': 'NC State',

  // UTEP
  'utep': 'UTEP',
  'texas el paso': 'UTEP',
  'ut el paso': 'UTEP',

  // Common "University of" patterns
  'university of alabama': 'Alabama',
  'bama': 'Alabama',
  'university of georgia': 'Georgia',
  'uga': 'Georgia',
  'university of oklahoma': 'Oklahoma',
  'university of florida': 'Florida',
  'uf': 'Florida',
  'university of clemson': 'Clemson',
  'university of oregon': 'Oregon',
  'ohio st': 'Ohio State',
  'ohio state university': 'Ohio State',
  'osu': 'Ohio State',
  'penn st': 'Penn State',
  'penn state university': 'Penn State',
  'psu': 'Penn State',
  'michigan st': 'Michigan State',
  'michigan state university': 'Michigan State',
  'msu': 'Michigan State',
  'mississippi st': 'Mississippi State',
  'mississippi state university': 'Mississippi State',
  'miss state': 'Mississippi State',
  'iowa st': 'Iowa State',
  'iowa state university': 'Iowa State',
  'utah st': 'Utah State',
  'utah state university': 'Utah State',
  'florida st': 'Florida State',
  'florida state university': 'Florida State',
  'fsu': 'Florida State',
  'fresno st': 'Fresno State',
  'fresno state university': 'Fresno State',
  'texas a&m university': 'Texas A&M',
  'texas am': 'Texas A&M',
  'texas a and m': 'Texas A&M',
  'tamu': 'Texas A&M',
  'georgia tech university': 'Georgia Tech',
  'georgia institute of technology': 'Georgia Tech',
  'ga tech': 'Georgia Tech',
  'notre dame university': 'Notre Dame',
  'university of notre dame': 'Notre Dame',
  'north dakota st': 'North Dakota State',
  'north dakota state university': 'North Dakota State',
  'ndsu': 'North Dakota State',
  'south dakota st': 'South Dakota State',
  'south dakota state university': 'South Dakota State',
  'sdsu': 'South Dakota State',
  'wisconsin university': 'Wisconsin',
  'university of wisconsin': 'Wisconsin',
  'university of michigan': 'Michigan',
  'u of m': 'Michigan',
  'stanford university': 'Stanford',
  'duke university': 'Duke',
  'purdue university': 'Purdue',
  'university of pittsburgh': 'Pittsburgh',
  'pitt': 'Pittsburgh',
  'university of iowa': 'Iowa',
  'northwestern university': 'Northwestern',
  'syracuse university': 'Syracuse',
  'university of maryland': 'Maryland',
  'university of louisville': 'Louisville',
  'university of cincinnati': 'Cincinnati',
  'cincy': 'Cincinnati',
  'university of tennessee': 'Tennessee',
  'ut': 'Tennessee',
  'university of texas': 'Texas',
  'ut austin': 'Texas',
  'texas tech university': 'Texas Tech',
  'university of wyoming': 'Wyoming',
  'boston college university': 'Boston College',
  'bc': 'Boston College',
  'university of arkansas': 'Arkansas',
  'arkansas state university': 'Arkansas State',
  'eastern michigan university': 'Eastern Michigan',
  'central michigan university': 'Central Michigan',
  'marshall university': 'Marshall',
  'rutgers university': 'Rutgers',
  'university of houston': 'Houston',
  'university of utah': 'Utah',
  'cal': 'California',
  'uc berkeley': 'California',
  'berkeley': 'California',
  'university of california': 'California',
  'washington state university': 'Washington State',
  'wsu': 'Washington State',
  'wazzu': 'Washington State',
  'university of washington': 'Washington',
  'uw': 'Washington',
  'udub': 'Washington',
  'north carolina university': 'North Carolina',
  'unc': 'North Carolina',
  'university of north carolina': 'North Carolina',
  'south carolina university': 'South Carolina',
  'university of south carolina': 'South Carolina',
  'arizona state university': 'Arizona State',
  'asu': 'Arizona State',
  'university of arizona': 'Arizona',
  'u of a': 'Arizona',
  'new mexico university': 'New Mexico',
  'university of new mexico': 'New Mexico',
  'troy university': 'Troy',
  'memphis university': 'Memphis',
  'university of memphis': 'Memphis',
  'university of buffalo': 'Buffalo',
  'west virginia university': 'West Virginia',
  'wvu': 'West Virginia',
  'tennessee chattanooga': 'Tennessee Chattanooga',
  'ut chattanooga': 'Tennessee Chattanooga',
  'utc': 'Tennessee Chattanooga',
  'chattanooga': 'Tennessee Chattanooga',
  'eastern washington university': 'Eastern Washington',
  'ewu': 'Eastern Washington',
  'western kentucky university': 'Western Kentucky',
  'wku': 'Western Kentucky',
  'eastern illinois university': 'Eastern Illinois',
  'eiu': 'Eastern Illinois',
  'alabama birmingham': 'Alabama Birmingham',
  'uab': 'Alabama Birmingham',
  'university of alabama birmingham': 'Alabama Birmingham',
  'west alabama university': 'West Alabama',
  'university of west alabama': 'West Alabama',

  // New colleges from expanded roster
  'auburn university': 'Auburn',
  'virginia tech university': 'Virginia Tech',
  'virginia polytechnic': 'Virginia Tech',
  'vt': 'Virginia Tech',
  'oklahoma state university': 'Oklahoma State',
  'okstate': 'Oklahoma State',
  'ok state': 'Oklahoma State',
  'university of colorado': 'Colorado',
  'cu': 'Colorado',
  'university of nebraska': 'Nebraska',
  'university of missouri': 'Missouri',
  'mizzou': 'Missouri',
  'university of kentucky': 'Kentucky',
  'uk': 'Kentucky',
  'vanderbilt university': 'Vanderbilt',
  'vandy': 'Vanderbilt',
  'harvard university': 'Harvard',
  'yale university': 'Yale',
  'university of virginia': 'Virginia',
  'uva': 'Virginia',
  'kansas state university': 'Kansas State',
  'k state': 'Kansas State',
  'ksu': 'Kansas State',
  'university of kansas': 'Kansas',
  'ku': 'Kansas',
  'kent state university': 'Kent State',
  'university of illinois': 'Illinois',
  'u of i': 'Illinois',
  'boise state university': 'Boise State',
  'boise st': 'Boise State',
  'ucf': 'UCF',
  'central florida': 'UCF',
  'university of central florida': 'UCF',
  'florida atlantic university': 'Florida Atlantic',
  'fau': 'Florida Atlantic',
  'san diego state university': 'San Diego State',
  'sdsu': 'San Diego State',
  'university of toledo': 'Toledo',
  'temple university': 'Temple',
  'university of connecticut': 'Connecticut',
  'uconn': 'Connecticut',
  'university of minnesota': 'Minnesota',
  'university of oregon': 'Oregon',
  'oregon state university': 'Oregon State',
  'oregon st': 'Oregon State',
  'university of delaware': 'Delaware',
  'university of nevada': 'Nevada',
  'unlv': 'UNLV',
  'louisiana tech university': 'Louisiana Tech',
  'la tech': 'Louisiana Tech',
  'mississippi valley state university': 'Mississippi Valley State',
  'mvsu': 'Mississippi Valley State',
  'jackson state university': 'Jackson State',
  'alcorn state university': 'Alcorn State',
  'savannah state university': 'Savannah State',
  'texas southern university': 'Texas Southern',
  'southern university': 'Southern University',
  'southern miss': 'Southern Mississippi',
  'university of southern mississippi': 'Southern Mississippi',
  'usm': 'Southern Mississippi',
  'james madison university': 'James Madison',
  'jmu': 'James Madison',
  'northern iowa university': 'Northern Iowa',
  'uni': 'Northern Iowa',
  'university of northern iowa': 'Northern Iowa',
  'colorado state university': 'Colorado State',
  'csu': 'Colorado State',
  'wake forest university': 'Wake Forest',
  'middle tennessee state university': 'Middle Tennessee',
  'middle tennessee': 'Middle Tennessee',
  'mtsu': 'Middle Tennessee',
  'grand valley state university': 'Grand Valley State',
  'idaho state university': 'Idaho State',
  'ucla': 'UCLA',
  'university of california los angeles': 'UCLA',
  'indiana university': 'Indiana',
  'university of indiana': 'Indiana',
  'utsa': 'UTSA',
  'university of texas san antonio': 'UTSA',
  'south carolina state university': 'South Carolina State',
  'sc state': 'South Carolina State',
  'east carolina university': 'East Carolina',
  'ecu': 'East Carolina',
  'coastal carolina university': 'Coastal Carolina',
  'weber state university': 'Weber State',
  'western michigan university': 'Western Michigan',
  'northern illinois university': 'Northern Illinois',
  'niu': 'Northern Illinois',
  'marist college': 'Marist',
  'georgia southern university': 'Georgia Southern',
  'minnesota state university': 'Minnesota State',
  'tulane university': 'Tulane',
  'john carroll university': 'John Carroll',
  'sonoma state university': 'Sonoma State',
  'hobart college': 'Hobart',
  'mount union': 'Mount Union',
  'university of mount union': 'Mount Union',
  'south alabama university': 'South Alabama',
  'university of south alabama': 'South Alabama',
  'louisiana lafayette': 'Louisiana-Lafayette',
  'university of louisiana lafayette': 'Louisiana-Lafayette',
  'ull': 'Louisiana-Lafayette',
  'arkansas pine bluff': 'Arkansas-Pine Bluff',
  'uapb': 'Arkansas-Pine Bluff',
  'san jose state university': 'San Jose State',
  'sjsu': 'San Jose State',
  'northern colorado university': 'Northern Colorado',
  'university of northern colorado': 'Northern Colorado',
  'kutztown university': 'Kutztown',
  'massachusetts university': 'Massachusetts',
  'umass': 'Massachusetts',
  'university of massachusetts': 'Massachusetts',
  'alabama a&m university': 'Alabama A&M',
  'virginia tech': 'Virginia Tech',
};

/**
 * Normalizes an input string by:
 * 1. Trimming whitespace
 * 2. Lowercasing
 * 3. Removing "university of" prefix and "university" suffix variations
 * 4. Checking alias map
 *
 * @param {string} input - user typed answer
 * @returns {string} - normalized college name
 */
export function normalizeCollege(input) {
  if (!input) return '';
  const trimmed = input.trim();
  const lower = trimmed.toLowerCase();

  // Direct alias lookup first
  if (ALIAS_MAP[lower]) {
    return ALIAS_MAP[lower];
  }

  // Return the original trimmed string if no alias match
  // (the fuzzy matcher will handle close-enough comparisons)
  return trimmed;
}

/**
 * Normalizes the canonical CSV college name for comparison purposes.
 * Lowercases and strips whitespace.
 *
 * @param {string} college - canonical college name from CSV
 * @returns {string} - lowered, trimmed version
 */
export function canonicalize(college) {
  return (college || '').trim().toLowerCase();
}
